// -----------------------------------------------------------------Imports---
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import jwt_decode from "jwt-decode";
import { createContext, useEffect, useState } from "react";
import { NavigateFunction, Route, Routes, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { GlobalDarkTheme, GlobalLightTheme, styles } from "./App.style";
import Drawer from "./components/drawer/Drawer";
import AccordionTableExample from "./components/examples/AccordionTable/AccordionTableExample";
import { getAppRoutes, isDark as GlobalIsDark } from "./Global";
import DeviceRequests from "./pages/deviceRequests/DeviceRequests";
import Devices from "./pages/devices/Devices";
import HomePage from "./pages/home/HomePage";
import LoginPage from "./pages/login/LoginPage";
import NotFoundPage from "./pages/notFound/NotFoundPage";
import Partners from "./pages/partners/Partners";
import TerminalRequests from "./pages/terminalRequests/TerminalRequests";
import Terminals from "./pages/terminals/Terminals";
import { setApiLogoutHandler } from "./services/apiClient";
import { initLoggerDiagnostics, logUnknownError } from "./services/logger/LoggerService";

import "react-toastify/dist/ReactToastify.min.css";

// -----------------------------------------------------------------Context---
export interface GlobalContextType {
  logOut: () => void;
}

export const GlobalContext: React.Context<GlobalContextType | null> = createContext<GlobalContextType | null>(null);

const getInitialUser = (): string => {
  const token: string | null = localStorage.getItem("micra_token");
  if (!token) return "";
  try {
    const decoded: { sub?: string } = jwt_decode<{ sub?: string }>(token.split(" ")[1]);
    return decoded.sub ?? "";
  } catch {
    return "";
  }
};

// -----------------------------------------------------------------Exports---
const App = (): JSX.Element => {
  const navigate: NavigateFunction = useNavigate();
  const [loggedInState, setLoggedInState] = useState<boolean>(!!localStorage.getItem("micra_token"));
  const [loggedInUser, setLoggedInUser] = useState<string>(getInitialUser());
  const [isBoxSmall, setBoxSmall] = useState<boolean>(false);
  const [_isDark, _setDark] = useState<boolean>(GlobalIsDark);

  useEffect((): void => {
    const currentPath: string = window.location.href.substring(window.location.href.lastIndexOf("/"));
    const exceptPaths: string[] = [getAppRoutes.login, getAppRoutes.registration];

    if (loggedInState && exceptPaths.indexOf(currentPath) !== -1) {
      navigate(getAppRoutes.home);
    } else if (!loggedInState && exceptPaths.indexOf(currentPath) === -1) {
      navigate(getAppRoutes.login);
    }
  }, [loggedInState, navigate]);

  const logOut = (): void => {
    localStorage.removeItem("micra_token");
    setLoggedInState(false);
    setLoggedInUser("");
  };

  // Wire up the API client's logout handler
  useEffect((): void => {
    setApiLogoutHandler(logOut);
  }, []);

  useEffect((): void => {
    initLoggerDiagnostics();
  }, []);

  useEffect((): (() => void) => {
    const previousOnError = window.onerror;

    window.onerror = (event: string | Event, source?: string, lineno?: number, colno?: number, error?: Error): void => {
      logUnknownError("window.onerror", error, {
        event: typeof event === "string" ? event : event.type,
        source,
        lineno,
        colno,
      });

      if (typeof previousOnError === "function") {
        previousOnError(event, source, lineno, colno, error);
      }
    };

    return (): void => {
      window.onerror = previousOnError;
    };
  }, []);

  // --------------------------------------------------------------Return---
  return (
    <ThemeProvider theme={_isDark ? GlobalDarkTheme : GlobalLightTheme}>
      <CssBaseline enableColorScheme />
      <ToastContainer theme={"dark"} position={"bottom-right"} />
      <GlobalContext.Provider value={{ logOut: logOut }}>
        <Drawer
          logOut={logOut}
          loggedInState={loggedInState}
          loggedInUser={loggedInUser}
          setBoxSmall={setBoxSmall}
          setDark={_setDark}
        />
        <Box
          style={!loggedInState ? styles.loggedOutBox : isBoxSmall ? styles.loggedInSmallBox : styles.loggedInBigBox}
        >
          <Routes>
            <Route
              element={
                <LoginPage
                  setLoggedInState={setLoggedInState}
                  setLoggedInUser={setLoggedInUser}
                  loggedInState={loggedInState}
                />
              }
              path={getAppRoutes.login}
            />
            <Route element={<HomePage />} path={getAppRoutes.home} />
            <Route element={<Partners />} path={getAppRoutes.partners} />
            <Route element={<AccordionTableExample />} path={getAppRoutes.accordionTableExample} />
            <Route element={<TerminalRequests />} path={getAppRoutes.terminalRequests} />
            <Route element={<Terminals />} path={getAppRoutes.terminals} />
            <Route element={<DeviceRequests />} path={getAppRoutes.deviceRequests} />
            <Route element={<Devices />} path={getAppRoutes.devices} />
            <Route element={<NotFoundPage />} path={getAppRoutes.notFound} />
          </Routes>
        </Box>
      </GlobalContext.Provider>
    </ThemeProvider>
  );
};

export default App;
